// src/services/pushService.js
// Web push на этом устройстве: поддержка, подписка, отписка, синхронизация с сервером.
// Service worker — public/sw.js; регистрируется в src/index.js (production, браузерный режим).
import { isTelegramMiniApp } from '../utils/platform';
import { isIos, isStandalone } from '../utils/device';
import { deletePushSubscription, fetchPushConfig, savePushSubscription } from './notificationsService';

export const SW_URL = '/sw.js';
const LOGOUT_UNSUBSCRIBE_TIMEOUT_MS = 3000;

export const PUSH_UNSUPPORTED = 'unsupported';
export const PUSH_IOS_INSTALL = 'ios-install';
export const PUSH_DENIED = 'denied';
export const PUSH_OFF = 'off';
export const PUSH_ON = 'on';

function browserSupportsPush() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

// Можно ли вообще показывать push в этом режиме (в Telegram — нет)
export function pushAvailableHere() {
  return !isTelegramMiniApp();
}

function urlBase64ToUint8Array(base64) {
  const padded = `${base64}${'='.repeat((4 - (base64.length % 4)) % 4)}`.replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(padded);
  return Uint8Array.from(raw, (char) => char.charCodeAt(0));
}

function sameKey(buffer, publicKey) {
  if (!buffer) return false;
  const a = new Uint8Array(buffer);
  const b = urlBase64ToUint8Array(publicKey);
  return a.length === b.length && a.every((byte, i) => byte === b[i]);
}

async function registration() {
  const existing = await navigator.serviceWorker.getRegistration('/');
  if (existing) return existing;
  // Обычно уже зарегистрирован из index.js; в dev-сборке — по первому «Включить»
  await navigator.serviceWorker.register(SW_URL, { scope: '/' });
  return navigator.serviceWorker.ready;
}

async function currentSubscription() {
  const reg = await navigator.serviceWorker.getRegistration('/');
  return reg ? reg.pushManager.getSubscription() : null;
}

let configPromise = null;
export function getPushConfig() {
  if (!configPromise) {
    configPromise = fetchPushConfig().catch((err) => {
      configPromise = null; // при ошибке — спросим снова в следующий раз
      throw err;
    });
  }
  return configPromise;
}

// Состояние для блока в настройках
export async function getPushState() {
  if (isIos() && !isStandalone()) return PUSH_IOS_INSTALL; // на iOS push — только в установленном приложении
  if (!browserSupportsPush()) return PUSH_UNSUPPORTED;
  if (Notification.permission === 'denied') return PUSH_DENIED;
  if (Notification.permission !== 'granted') return PUSH_OFF;
  return (await currentSubscription()) ? PUSH_ON : PUSH_OFF;
}

// Только по нажатию пользователя: браузеры блокируют запрос разрешения без жеста
export async function enablePush() {
  const config = await getPushConfig();
  if (!config.enabled) throw new Error('Push-уведомления не настроены на сервере');
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission === 'denied' ? PUSH_DENIED : PUSH_OFF;

  const reg = await registration();
  let subscription = await reg.pushManager.getSubscription();
  if (subscription && !sameKey(subscription.options?.applicationServerKey, config.public_key)) {
    await subscription.unsubscribe(); // подписка на старые ключи VAPID
    subscription = null;
  }
  if (!subscription) {
    subscription = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(config.public_key),
    });
  }
  await savePushSubscription(subscription.toJSON());
  return PUSH_ON;
}

export async function disablePush() {
  const subscription = await currentSubscription();
  if (!subscription) return PUSH_OFF;
  try {
    await deletePushSubscription(subscription.endpoint);
  } catch {
    // на сервере могло уже не быть — главное отписаться в браузере
  }
  await subscription.unsubscribe();
  return PUSH_OFF;
}

/**
 * При старте приложения: подписка браузера могла смениться (pushsubscriptionchange в sw.js)
 * или ключи VAPID на сервере поменялись — сверяем и отправляем актуальную на сервер.
 * Заодно привязывает устройство к текущему пользователю. Ничего не спрашивает у пользователя.
 */
export async function syncPushSubscription() {
  if (!pushAvailableHere() || !browserSupportsPush() || Notification.permission !== 'granted') return;
  try {
    const config = await getPushConfig();
    if (!config.enabled) return;
    const subscription = await currentSubscription();
    if (!subscription) return;
    if (!sameKey(subscription.options?.applicationServerKey, config.public_key)) {
      await enablePush(); // переподписка на новые ключи (разрешение уже есть — без запроса)
      return;
    }
    await savePushSubscription(subscription.toJSON());
  } catch (err) {
    console.warn('Push: подписка не синхронизирована:', err?.message || err);
  }
}

// Перед выходом из аккаунта: следующий пользователь устройства не должен получать чужие push
export async function unsubscribeOnLogout() {
  if (!pushAvailableHere() || !browserSupportsPush()) return;
  const timeout = new Promise((resolve) => setTimeout(resolve, LOGOUT_UNSUBSCRIBE_TIMEOUT_MS));
  try {
    await Promise.race([disablePush(), timeout]);
  } catch (err) {
    console.warn('Push: отписка при выходе не удалась:', err?.message || err);
  }
}

export function registerServiceWorker() {
  if (!pushAvailableHere() || !('serviceWorker' in navigator) || process.env.NODE_ENV !== 'production') return;
  window.addEventListener('load', () => {
    navigator.serviceWorker.register(SW_URL, { scope: '/' }).catch((err) => {
      console.warn('Service worker не зарегистрирован:', err?.message || err);
    });
  });
}
